import { Suspense, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  BarChart3,
  Bell,
  Box,
  ChevronRight,
  ClipboardList,
  Coins,
  FileText,
  HandCoins,
  LayoutGrid,
  LogOut,
  Moon,
  Plus,
  Receipt,
  MapPinned,
  Route,
  Search,
  Settings,
  Store,
  Ticket,
  Sun,
  Trash2,
  Truck,
  User,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react'
import { calcularNotificaciones, useDatos } from '../store/datos'
import { useSesion } from '../store/sesion'
import { useUI } from '../store/ui'
import { cn } from '../lib/cn'
import { liberarTodoElScroll } from '../lib/bloqueoScroll'
import { Avatar, BotonIcono, Cargando, Chip } from './ui/base'
import { Hoja } from './ui/Hoja'
import { Logo, Isotipo } from './Logo'
import { Buscador } from './Buscador'
import { Avisos } from './ui/Avisos'

interface ItemNav {
  ruta: string
  etiqueta: string
  icono: typeof Box
  soloAdmin?: boolean
  soloVendedor?: boolean
}

const NAV: ItemNav[] = [
  { ruta: '/', etiqueta: 'Inicio', icono: LayoutGrid },
  { ruta: '/inventario', etiqueta: 'Inventario', icono: Box },
  // NovaBerry (administración) no vende: esta pantalla es solo para
  // cuentas de vendedor.
  { ruta: '/vender', etiqueta: 'Nueva venta', icono: Plus, soloVendedor: true },
  // El mapa de locales por visitar. Lo ven vendedores y administración:
  // el vendedor para no repetir un viaje que ya hizo un compañero, el
  // administrador para ver cómo va la cobertura de la región.
  { ruta: '/vender/bitacora', etiqueta: 'Bitácora de ventas', icono: MapPinned },
  { ruta: '/ventas', etiqueta: 'Ventas', icono: Receipt },
  // Vista completa de la cartera de clientes —con última compra y
  // descarga a Excel—: solo el administrador la ve en el menú. El
  // vendedor sigue llegando a "/clientes" para elegir a quién vender,
  // pero desde dentro de "Nueva venta", no desde acá.
  { ruta: '/clientes', etiqueta: 'Clientes', icono: Users, soloAdmin: true },
  { ruta: '/perfil', etiqueta: 'Perfil', icono: User },
  { ruta: '/pedidos', etiqueta: 'Pedidos', icono: Truck },
  { ruta: '/despachos', etiqueta: 'Despachos', icono: Route, soloAdmin: true },
  // Los locales físicos de NovaBerry POS: ambas apps comparten la misma
  // base de datos, así que esto solo lee la tabla que POS ya mantiene.
  { ruta: '/sucursales', etiqueta: 'Sucursales', icono: Store, soloAdmin: true },
  { ruta: '/suscriptores', etiqueta: 'Suscriptores', icono: Ticket, soloAdmin: true },
  { ruta: '/cobranza', etiqueta: 'Cobranza', icono: Wallet },
  { ruta: '/reportes', etiqueta: 'Reportes', icono: FileText, soloAdmin: true },
  { ruta: '/estadisticas', etiqueta: 'Estadísticas', icono: BarChart3, soloAdmin: true },
  { ruta: '/gastos', etiqueta: 'Gastos', icono: HandCoins, soloAdmin: true },
  // Lo que hay que pagarle al equipo: junta las comisiones que ya viven
  // congeladas en cada venta con los viajes del despachador.
  { ruta: '/pagos', etiqueta: 'Pagos', icono: Coins, soloAdmin: true },
  { ruta: '/devoluciones', etiqueta: 'Devoluciones', icono: ClipboardList },
  { ruta: '/usuarios', etiqueta: 'Vendedores', icono: UserCog, soloAdmin: true },
  // La bitácora de empleados ya no va en el menú: vive dentro de
  // Reportes. Tener dos "Bitácora" en la misma lista —esta y el mapa de
  // ventas— obligaba a adivinar cuál era cuál.
  { ruta: '/ajustes', etiqueta: 'Ajustes', icono: Settings },
]

/** Las cinco de la barra inferior en teléfono. "Vender" va al centro y
 *  elevada porque es la acción que se repite cien veces al día. */
const TABS = ['/', '/inventario', '/vender', '/ventas', '/perfil']

export function Layout() {
  const usuario = useSesion((s) => s.usuario)
  const salir = useSesion((s) => s.salir)
  const { tema, setTema, setBuscador, notiVistas, notiOcultas, marcarNotiVistas, eliminarNotiLeidas } =
    useUI()
  const datos = useDatos()
  const navegar = useNavigate()
  const { pathname } = useLocation()
  const [panelAvisos, setPanelAvisos] = useState(false)
  const [menuPerfil, setMenuPerfil] = useState(false)

  const esAdmin = usuario?.rol === 'admin'
  const esVendedor = usuario?.rol === 'vendedor'
  const nav = NAV.filter((n) => (!n.soloAdmin || esAdmin) && (!n.soloVendedor || esVendedor))
  // Las que ya se eliminaron no vuelven a aparecer —salvo que la misma
  // alerta vuelva a ser cierta con datos nuevos, en cuyo caso su id
  // cambia y no queda tapada por el filtro de acá abajo.
  const notificaciones = useMemo(() => {
    const todas = calcularNotificaciones(datos, usuario ? { usuarioId: usuario.id, esAdmin } : undefined)
    return todas.filter((n) => !notiOcultas.includes(n.id))
  }, [datos, usuario, esAdmin, notiOcultas])
  const sinLeer = useMemo(
    () => notificaciones.filter((n) => !notiVistas.includes(n.id)),
    [notificaciones, notiVistas],
  )
  const leidas = useMemo(
    () => notificaciones.filter((n) => notiVistas.includes(n.id)),
    [notificaciones, notiVistas],
  )
  const tabs = nav.filter((n) => TABS.includes(n.ruta))

  // Red de seguridad del scroll: cambiar de ruta desmonta las hojas y el
  // buscador de la pantalla anterior, así que acá nunca debería quedar
  // nada bloqueando. Si quedó, se escapó algo —y sin esto la única
  // salida sería recargar la app.
  useEffect(() => {
    liberarTodoElScroll()
  }, [pathname])

  // Al cerrar el panel, todo lo que se alcanzó a mostrar queda marcado
  // como visto —así la próxima vez que se abra, el "Eliminar leídas"
  // ya tiene qué borrar.
  const cerrarPanelAvisos = () => {
    setPanelAvisos(false)
    marcarNotiVistas(notificaciones.map((n) => n.id))
  }

  // En las rutas de detalle (/ventas/:id) no hay entrada de menú, así
  // que se usa el nombre de la sección padre.
  // "Clientes" ya no tiene su propia entrada en el menú —vive dentro de
  // Ventas—, así que su título se resuelve aparte, igual que "Perfil".
  const titulo =
    nav.find((n) => n.ruta === pathname)?.etiqueta ??
    nav.find((n) => n.ruta !== '/' && pathname.startsWith(n.ruta))?.etiqueta ??
    (pathname.startsWith('/vendedores')
      ? 'Perfil'
      : pathname.startsWith('/clientes')
        ? 'Clientes'
        : 'NovaBerry')

  const alternarTema = () => setTema(tema === 'oscuro' ? 'claro' : 'oscuro')
  const esOscuro = document.documentElement.classList.contains('dark')

  // Despachos no es una persona con su propio menú: es una pantalla
  // sola. Nada de barra lateral ni de pestañas abajo —solo la ruta del
  // día, bien grande y fácil de leer.
  if (usuario?.rol === 'despacho') {
    return (
      <div className="flex min-h-dvh flex-col superficie">
        <header className="sticky top-0 z-30 border-b borde-suave superficie-barra safe-top">
          <div className="flex h-20 items-center gap-3 px-4 sm:px-6">
            <img
              src="/logo-despachos.jpeg"
              alt="NovaBerry Despachos"
              className="size-14 shrink-0 rounded-2xl object-cover"
            />
            <h1 className="min-w-0 flex-1 truncate text-lg font-bold texto-base">Despachos</h1>
            <BotonIcono etiqueta={esOscuro ? 'Modo claro' : 'Modo oscuro'} onClick={alternarTema}>
              {esOscuro ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </BotonIcono>
            <button
              onClick={async () => {
                await salir()
                navegar('/entrar')
              }}
              className="grid size-10 place-items-center rounded-xl texto-suave transition-colors hover:superficie-hundida hover:text-rojo-500"
              aria-label="Cambiar de usuario"
            >
              <LogOut className="size-5" />
            </button>
          </div>
        </header>
        <main className="flex-1 safe-bottom">
          <Suspense fallback={<Cargando />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-dvh superficie">
      {/* ---------------- Barra lateral (escritorio) ---------------- */}
      <aside className="sticky top-0 hidden h-dvh w-64 shrink-0 flex-col border-r borde-suave superficie-elevada lg:flex">
        <div className="px-5 py-6">
          <Logo conBajada />
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 pb-4 sin-scrollbar">
          {nav.map((n) => {
            const perfilActivo = n.ruta === '/perfil' && pathname === `/vendedores/${usuario?.id}`
            // El vendedor no tiene entrada propia de "Clientes" en el
            // menú (solo llega ahí desde "Nueva venta"), así que al
            // mirarlo se resalta "Ventas" en su lugar. El administrador
            // sí tiene su propia entrada, así que no necesita este atajo.
            const ventasActivo = n.ruta === '/ventas' && !esAdmin && pathname.startsWith('/clientes')
            return (
              <NavLink
                key={n.ruta}
                to={n.ruta}
                end={n.ruta === '/'}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[14px] font-semibold transition-all duration-200',
                    isActive || perfilActivo || ventasActivo
                      ? 'bg-bosque-500 text-white shadow-sm shadow-bosque-500/25'
                      : 'texto-suave hover:superficie-hundida hover:texto-base',
                  )
                }
              >
                <n.icono className="size-[18px] shrink-0" />
                <span className="truncate">{n.etiqueta}</span>
              </NavLink>
            )
          })}
        </nav>

        <div className="border-t borde-suave p-3">
          <button
            onClick={() => setMenuPerfil(true)}
            className="flex w-full items-center gap-3 rounded-2xl p-2 text-left transition-colors hover:superficie-hundida"
          >
            <Avatar nombre={usuario?.nombre ?? '?'} tamano="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold texto-base">
                {usuario?.nombre}
              </span>
              <span className="block truncate text-[11px] texto-tenue">
                {esAdmin ? 'Administrador' : 'Vendedor'}
              </span>
            </span>
            <ChevronRight className="size-4 texto-tenue" />
          </button>
        </div>
      </aside>

      {/* ---------------- Contenido ---------------- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 border-b borde-suave superficie-barra safe-top">
          <div className={cn('flex items-center gap-2 px-4 sm:px-6', pathname === '/despachos' ? 'h-20 gap-3' : 'h-16')}>
            {pathname === '/despachos' ? (
              <img
                src="/logo-despachos.jpeg"
                alt="NovaBerry Despachos"
                className="size-14 shrink-0 rounded-2xl object-cover"
              />
            ) : (
              <div className="lg:hidden">
                <Isotipo className="size-8" />
              </div>
            )}
            <h1 className="min-w-0 flex-1 truncate text-lg font-bold texto-base lg:text-xl">
              {titulo}
            </h1>

            <BotonIcono etiqueta="Buscar" onClick={() => setBuscador(true)}>
              <Search className="size-5" />
            </BotonIcono>

            <BotonIcono etiqueta="Alertas" onClick={() => setPanelAvisos(true)} className="relative">
              <Bell className="size-5" />
              {sinLeer.length > 0 && (
                <span className="absolute top-1.5 right-1.5 grid size-[18px] place-items-center rounded-full bg-rojo-500 text-[10px] font-bold text-white">
                  {sinLeer.length > 9 ? '9+' : sinLeer.length}
                </span>
              )}
            </BotonIcono>

            <BotonIcono etiqueta={esOscuro ? 'Modo claro' : 'Modo oscuro'} onClick={alternarTema}>
              {esOscuro ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </BotonIcono>

            <button
              onClick={() => setMenuPerfil(true)}
              className="ml-1 lg:hidden"
              aria-label="Tu cuenta"
            >
              <Avatar nombre={usuario?.nombre ?? '?'} tamano="sm" />
            </button>
          </div>
        </header>

        <main className="flex-1 pb-24 lg:pb-8">
          <Suspense fallback={<Cargando />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* ---------------- Barra inferior (teléfono) ---------------- */}
      <nav
        className="fixed inset-x-3 z-40 rounded-[28px] border-2 border-bosque-400 superficie-barra sombra-flotante lg:hidden"
        style={{ bottom: 'calc(0.625rem + env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto flex max-w-lg items-center justify-around px-2 pt-0.5 pb-0.5">
          {tabs.map((t) => {
            const central = t.ruta === '/vender'
            // "/perfil" reenvía de inmediato a "/vendedores/mi-id": la URL
            // real ya no es "/perfil" cuando se está mirando el perfil, así
            // que el resaltado automático de NavLink no lo detectaría solo.
            const esPerfil = t.ruta === '/perfil'
            const perfilActivo = esPerfil && pathname === `/vendedores/${usuario?.id}`
            const ventasActivo = t.ruta === '/ventas' && !esAdmin && pathname.startsWith('/clientes')
            return (
              <NavLink
                key={t.ruta}
                to={t.ruta}
                end={t.ruta === '/'}
                className={({ isActive }) =>
                  cn(
                    'flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-2xl py-0.5 transition-colors',
                    central && 'relative -mt-5',
                    !central &&
                      (isActive || perfilActivo || ventasActivo
                        ? 'text-bosque-500 dark:text-bosque-200'
                        : 'texto-tenue'),
                  )
                }
              >
                {({ isActive: activoPorRuta }) => {
                  const isActive = activoPorRuta || perfilActivo || ventasActivo
                  return central ? (
                    <>
                      <span
                        className={cn(
                          'grid size-16 place-items-center transition-all duration-300 ease-[var(--ease-suave)]',
                          isActive && 'scale-105',
                        )}
                      >
                        <img
                          src="/logo-vender.png"
                          alt="Vender"
                          className="size-16 object-contain drop-shadow-lg"
                        />
                      </span>
                      <span className="text-[10px] font-bold texto-suave">Vender</span>
                    </>
                  ) : (
                    <>
                      <t.icono className="size-6" strokeWidth={isActive ? 2.6 : 2} />
                      <span className="text-[10px] font-semibold">{t.etiqueta}</span>
                    </>
                  )
                }}
              </NavLink>
            )
          })}
        </div>
      </nav>

      {/* ---------------- Superpuestos ---------------- */}
      <Buscador />
      <Avisos />

      <Hoja
        abierta={panelAvisos}
        onCerrar={cerrarPanelAvisos}
        titulo="Alertas"
        descripcion={
          notificaciones.length
            ? `${notificaciones.length} ${notificaciones.length === 1 ? 'asunto' : 'asuntos'} que necesitan atención`
            : 'Todo en orden por ahora'
        }
      >
        {notificaciones.length === 0 ? (
          <p className="py-10 text-center text-sm texto-tenue">
            Sin stock crítico, vencimientos ni cobros atrasados.
          </p>
        ) : (
          <>
            {/* Solo se puede borrar lo que ya se vio antes —abrir el
                panel y volver a abrirlo es lo que las marca como
                leídas—, así nadie hace desaparecer sin querer una
                alerta que recién apareció. */}
            {leidas.length > 0 && (
              <button
                onClick={() => eliminarNotiLeidas(leidas.map((n) => n.id))}
                className="mb-2 inline-flex items-center gap-1.5 text-[13px] font-semibold text-bosque-500 transition-colors hover:underline dark:text-bosque-200"
              >
                <Trash2 className="size-3.5" />
                Eliminar leídas ({leidas.length})
              </button>
            )}
            <ul className="space-y-2 pb-2 escalonar">
              {notificaciones.map((n) => {
                const leida = notiVistas.includes(n.id)
                return (
                  <li key={n.id}>
                    <button
                      onClick={() => {
                        cerrarPanelAvisos()
                        if (n.enlace) navegar(n.enlace)
                      }}
                      className={cn(
                        'flex w-full items-start gap-3 rounded-2xl border borde-suave p-3.5 text-left transition-colors hover:superficie-hundida',
                        leida && 'opacity-60',
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 size-2.5 shrink-0 rounded-full',
                          n.tipo === 'agotado'
                            ? 'bg-rojo-500'
                            : n.tipo === 'stock_bajo'
                              ? 'bg-ambar-400'
                              : n.tipo === 'vencimiento'
                                ? 'bg-magenta-500'
                                : n.tipo === 'seguimiento'
                                  ? 'bg-[#d4a017]'
                                  : 'bg-tinta-500',
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="block truncate text-[15px] font-semibold texto-base">
                            {n.titulo}
                          </span>
                          {leida && (
                            <span className="shrink-0 text-[10px] font-bold tracking-wide texto-tenue uppercase">
                              Leída
                            </span>
                          )}
                        </span>
                        <span className="block text-[13px] texto-suave">{n.detalle}</span>
                      </span>
                      <ChevronRight className="mt-1 size-4 shrink-0 texto-tenue" />
                    </button>
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </Hoja>

      <Hoja
        abierta={menuPerfil}
        onCerrar={() => setMenuPerfil(false)}
        titulo="Tu cuenta"
        ancho="sm"
      >
        <button
          onClick={() => {
            setMenuPerfil(false)
            navegar(`/vendedores/${usuario?.id}`)
          }}
          className="flex w-full items-center gap-4 rounded-2xl superficie-hundida p-4 text-left transition-opacity hover:opacity-80"
        >
          <Avatar nombre={usuario?.nombre ?? '?'} tamano="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-bold texto-base">{usuario?.nombre}</p>
            <p className="truncate text-sm texto-suave">{usuario?.email}</p>
            <Chip className="mt-1.5 bg-bosque-50 text-bosque-600 dark:bg-bosque-500/20 dark:text-bosque-200">
              {esAdmin ? 'Administrador' : 'Vendedor'}
            </Chip>
          </div>
          <ChevronRight className="size-4 shrink-0 texto-tenue" />
        </button>

        <div className="mt-4 space-y-1">
          {nav
            .filter((n) => !TABS.includes(n.ruta))
            .map((n) => (
              <button
                key={n.ruta}
                onClick={() => {
                  setMenuPerfil(false)
                  navegar(n.ruta)
                }}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-[15px] font-semibold texto-base transition-colors hover:superficie-hundida"
              >
                <n.icono className="size-[18px] texto-suave" />
                {n.etiqueta}
                <ChevronRight className="ml-auto size-4 texto-tenue" />
              </button>
            ))}
          <button
            onClick={async () => {
              setMenuPerfil(false)
              await salir()
              navegar('/entrar')
            }}
            className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-[15px] font-semibold text-rojo-500 transition-colors hover:bg-rojo-50 dark:hover:bg-rojo-500/10"
          >
            <LogOut className="size-[18px]" />
            Cambiar de vendedor
          </button>
        </div>
      </Hoja>
    </div>
  )
}

/** Contenedor estándar de página: ancho máximo y respiración. */
export function Pagina({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('mx-auto w-full max-w-6xl px-4 py-5 sm:px-6 sm:py-7', className)}>
      {children}
    </div>
  )
}

/** Encabezado de sección con acción opcional a la derecha. */
/** Vuelve a donde se estaba antes —el historial del navegador, no una
 *  ruta fija— para que "Volver" desde Sucursales lleve a Inicio si se
 *  entró desde ahí, o a Ajustes si se entró desde ahí. Cuando no hay
 *  historial propio (se abrió la URL directo, o es la primera pantalla
 *  de la sesión) cae a Inicio en vez de dejar a la persona en blanco. */
export function BotonVolver({ className }: { className?: string }) {
  const navegar = useNavigate()
  return (
    <button
      onClick={() => {
        if (window.history.state?.idx > 0) navegar(-1)
        else navegar('/')
      }}
      className={cn(
        'mb-3 inline-flex items-center gap-1.5 text-[14px] font-semibold texto-suave transition-colors hover:texto-base',
        className,
      )}
    >
      <ArrowLeft className="size-4" /> Volver
    </button>
  )
}

export function Encabezado({
  titulo,
  detalle,
  accion,
  atras = false,
}: {
  titulo: string
  detalle?: string
  accion?: React.ReactNode
  /** Pantallas del menú administrador a las que se llega desde otra
   *  parte (no las cinco de la barra inferior): muestran "Volver"
   *  arriba del título. */
  atras?: boolean
}) {
  return (
    <div className="mb-5">
      {atras && <BotonVolver />}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold texto-base sm:text-[28px]">{titulo}</h2>
          {detalle && <p className="mt-1 text-sm texto-suave">{detalle}</p>}
        </div>
        {accion}
      </div>
    </div>
  )
}
